import Vue from 'vue'
import VueRouter, { Location, Route } from 'vue-router'
import { CompanyAPI, OrderAPI, ReferralAPI, UserAPI } from '@/api/backend-client'
import { onSessionExpired } from '@/api/auth-client'
import routes from '@/router/routes'

Vue.use(VueRouter)

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
})

const openOneUrl = (url: string) => {
    const urlParams = new URLSearchParams({
        token: UserAPI.refreshToken,
        redirect: `${process.env.VUE_APP_ONE_APP_BASEURL}${url}`,
    })

    window.open(
        `${process.env.VUE_APP_ONE_APP_BASEURL}/authenticate?` + urlParams.toString(),
        '_blank',
    )
}

const redirectOneUrl = (url: string) => {
    const urlParams = new URLSearchParams({
        token: UserAPI.refreshToken,
        redirect: `${process.env.VUE_APP_ONE_APP_BASEURL}${url}`,
    })

    window.location.href = `${process.env.VUE_APP_ONE_APP_BASEURL}/authenticate?` + urlParams.toString()
}

/** The login page, returning to the full current location (query and hash included) after signing in */
const loginLocation = (route: Route): Location => ({
    name: 'user_login',
    query: {
        redirect: route.fullPath,
    },
})

router.beforeEach((to, from, next) => {
    if (to.params.toOne) {
        // Reroute naar /contact/lead for Beta Keyvue
        if (to.name.startsWith('tasks_detail')) {
            openOneUrl(`/contact/${to.params.contactId}/lead/${to.params.referralId}`)
            return next(false)
        }

        // Reroute naar /contact/order for Beta Keyvue
        if (to.name.startsWith('orders_detail')) {
            openOneUrl(`/contact/${to.params.contactId}/order/${to.params.orderId}`)
            return next(false)
        }

        if (to.name === 'one_contact_detail') {
            openOneUrl(`/contact/${to.params.contactId}`)
            return next(false)
        }

        if (to.name === 'one_settings_virtual_colleague_ai') {
            openOneUrl(`/settings/virtual-colleague/ai`)
            return next(false)
        }
    }

    next()
})

router.beforeResolve(async (to, from, next) => {
    // Skip any permission checks when a route is marked anonymous.
    if (to.meta.anonymous === true) {
        return next()
    }

    // Ensure user and company data are loaded before continuing, otherwise force log out.
    if (!UserAPI.isAuthenticated || !await UserAPI.getCurrentUser() || !await CompanyAPI.getCurrentCompany()) {
        await UserAPI.logout()
        return next(loginLocation(to))
    }

    // Check if route has role requirements.
    if (to.meta.requiredRole) {
        const currentUser = await UserAPI.getCurrentUser()

        // If user does not have required role.
        if (currentUser.hasRole(to.meta.requiredRole) === false) {

            // Abort navigation
            return next(false)
        }
    }

    if (to.meta.superAdmin) {
        const currentUser = await UserAPI.getCurrentUser()
        if (!currentUser.superAdmin) {
            return next ({
                name: 'dashboard',
            })
        }
    }

    if (to.meta.condition) {
        const result = await to.meta.condition()
        if (!result) {

            // Abort navigation
            return next(false)
        }
    }

    if (to.name === 'documents') {
        window.location.href = `${ process.env.VUE_APP_ONE_APP_BASEURL }/document?token=${ UserAPI.refreshToken }`

        return next(false)
    }

    let isOneRoute = false
    const oneRoutes = ['tasks_detail', 'orders_detail']
    oneRoutes.forEach(routeName => {
        if (to.name.startsWith(routeName)) {
            isOneRoute = true
        }
    })

    if (isOneRoute) {
        const currentUser = await UserAPI.getCurrentUser()
        if (currentUser.betaOptInEnabled) {
            // Reroute naar /contact/lead for Beta Keyvue
            if (to.name.startsWith('tasks_detail')) {
                const { contact } = await ReferralAPI.get(to.params.referralId)
                redirectOneUrl(`/contact/${contact.id}/lead/${to.params.referralId}`)
                return next(false)
            }

            // Reroute naar /contact/order for Beta Keyvue
            if (to.name.startsWith('orders_detail')) {
                const { referral: { contact } } = await OrderAPI.get(to.params.orderId)
                redirectOneUrl(`/contact/${contact.id}/order/${to.params.orderId}`)
                return next(false)
            }
        }
    }

    next()
})

// A session that can no longer be refreshed ends on the login page, not on a page that silently stops loading. As a full
// page load, so none of the ended session's store or caches carry over to whoever signs in next on this computer.
// Until the first navigation has finished (no matched route yet) the guard above handles it, with the page that was asked
// for; anonymous pages need no session. Replace, so Back doesn't return to the page that no longer loads.
onSessionExpired(() => {
    if (router.currentRoute.matched.length === 0 || router.currentRoute.meta.anonymous === true) {
        return
    }

    window.location.replace(router.resolve(loginLocation(router.currentRoute)).href)
})

export default router
